import { type ComponentProps, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { Badge } from './badge';

interface PanelProps extends Omit<ComponentProps<'section'>, 'title'> {
  title: ReactNode;
  /** Shown as a badge next to the title; left out, no badge. */
  count?: number;
  /** Header controls, right-aligned. A slot, since they often swap on selection. */
  actions?: ReactNode;
  /** The title's heading level, h2 by default; deeper when the Panel nests in a section. */
  titleAs?: 'h2' | 'h3' | 'h4' | 'h5';
}

/**
 * A titled list: the header sits on the card colour and the list in a well of the
 * page colour inside it — the frame is what lifts the rows off a sheet or a form,
 * not a border. The caller renders its own empty state in the well, and caps
 * its own list when it scrolls, so a sticky table header sticks to that box.
 */
export const Panel = ({
  title,
  count,
  actions,
  titleAs: Title = 'h2',
  className,
  children,
  ...props
}: PanelProps) => (
  <section
    data-slot="panel"
    className={cn(
      'flex flex-col rounded-xl bg-card px-2 pb-2 text-card-foreground',
      className,
    )}
    {...props}
  >
    <header className="flex min-h-12 flex-wrap items-center gap-x-3 gap-y-2 px-2 py-1.5">
      <Title className="flex flex-1 items-center gap-2 text-sm font-semibold">
        {title}
        {count !== undefined && (
          <Badge className="rounded-full bg-background font-normal text-muted-foreground">
            {count}
          </Badge>
        )}
      </Title>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
    <div data-slot="panel-body" className="rounded-lg bg-background p-2">
      {children}
    </div>
  </section>
);
